import { Injectable, NgZone } from '@angular/core';
import { IdexService } from '../idex.service';
import { Observable } from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import { Toast } from '../shared/toast/toast';

@Injectable()
export class FileUploader {
   private chunkSize = 1048576;
   busy: boolean;

   constructor(private translate: TranslateService, private service: IdexService, private zone: NgZone, private toast: Toast) { }

   process(dataTransfer: DataTransfer, maxFileSize: number, path: string, tin: string, callback: any, folderId?: string): void {
      this.busy = true;
      if (dataTransfer.items) {
         const item = dataTransfer.items[0];
         if (item.webkitGetAsEntry) {
            const list = [];
            const dataTransferItem = dataTransfer.items;
            for (let i = 0; i < dataTransferItem.length; i++) {
               list.push(dataTransferItem[i].webkitGetAsEntry());
            }
            this.getFilesAsync(list).then(files => {
               this.uploadFiles(files, maxFileSize, path, tin, callback, null, folderId);
            }, () => {
               this.uploadFiles(dataTransfer.files, maxFileSize, path, tin, callback, null, folderId);
            });
         } else {
            this.uploadFiles(dataTransfer.files, maxFileSize, path, tin, callback, null, folderId);
         }
      } else {
         this.uploadFiles(dataTransfer.files, maxFileSize, path, tin, callback, null, folderId);
      }
   }

   async getFilesAsync(entries) {
      const files: File[] = [];
      for (let i = 0; i < entries.length; i++) {
         const entryContent = await this.readEntryContentAsync(entries[i]);
         files.push(...entryContent);
      }
      return files;
   }

   readEntryContentAsync(entry: any) {
      return new Promise<File[]>((resolve, reject) => {
         let reading = 0;
         const contents: File[] = [];

         const readEntry = (item) => {
            if (item.isFile) {
               reading++;
               item.file(file => {
                  reading--;
                  const name = file.name.toLowerCase();
                  if (name !== 'thumbs.db' && name !== 'desktop.ini' && name !== '.ds_store') {
                     file.fullPath = item.fullPath;
                     contents.push(file);
                  }
                  if (reading === 0) {
                     resolve(contents);
                  }
               }, error => reject(error));
            } else if (item.isDirectory) {
               const reader = item.createReader();
               readReaderContents(reader);
            }
         };

         const readReaderContents = (reader: any) => {
            reading++;
            reader.readEntries( (entries) => {
               reading--;
               for (const ent of entries) {
                  readEntry(ent);
               }

               if (entries.length !== 0) {
                  readReaderContents(reader);
               }

               if (reading === 0) {
                  resolve(contents);
               }
            });
         };

         readEntry(entry);
      });
   }

   private readEntries(dir: any, callback: any, error: any): void {
      callback.folders = callback.folders || [];
      callback.files = callback.files || [];
      callback.level = callback.level || 0;
      const reader = dir.createReader();
      const readEntries = () => {
         callback.level++;
         reader.readEntries((entries) => {
            callback.level--;
            for (let i = 0; i < entries.length; i++) {
               if (entries[i].isDirectory) {
                  callback.folders[entries[i].fullPath] = [];
                  this.readEntries(entries[i], callback, error);
               } else {
                  callback.level++;
                  entries[i].file((file) => {
                     const name = file.name.toLowerCase();
                     if (name !== 'thumbs.db' && name !== 'desktop.ini' && name !== '.ds_store') {
                        file.fullPath = dir.fullPath === '/' ? file.name : dir.fullPath + '/' + file.name;
                        callback.files.push(file);
                     }
                     if (--callback.level === 0) {
                        callback(callback.folders, callback.files);
                     }
                  }, error);
               }
            }

            if (callback.level === 0) {
               callback(callback.folders, callback.files);
            }
         });
      };
      readEntries();
   }

   private getLeafFolder(folders: any): string[] {
      const leaf: string[] = [];
      Object.keys(folders).forEach((key) => {
         if (folders[key].length === 0) {
            leaf.push(key);
         }
      });

      // collapse folder list by removing folders with empty parents - we want empty leaf nodes only
      for (let d, n = 0; n < leaf.length; n++) {
         d = leaf[n];
         for (let m = 0; m < leaf.length; m++) {
            if (m !== n && leaf[m].indexOf(d) === 0) {
               leaf.splice(n--, 1);
               break;
            }
         }
      }
      return leaf;
   }

   private createFolders(path: string, folders: any, callback: any) {
      const leaf = this.getLeafFolder(folders);
      if (leaf.length === 0) {
         callback();
         return;
      }
      this.service.post('/folders', { destination: path, folders: leaf })
         .subscribe(() => {
            callback();
         }, () => { callback(); });
   }

   uploadFolder(list: FileList, maxFileSize: number, path: string, tin: string, callback: any, id?: string, folderId?: string, isMajor?: boolean, annotationObj?: any): void {
      this.busy = true;
      const files = [];
      for (let i = 0; i < list.length; i++) {
         const file: any = list.item(i);
         file.fullPath = this.trimName(file.webkitRelativePath);
         files.push(file);
      }
      this.uploadFiles(files, maxFileSize, path, tin, callback, id, folderId, isMajor);
   }

   trimName(fullPath: string): string | null {
      const resultArray: string[] = fullPath.split('/').map(item => item.trim());
      const mergedString: string = resultArray.join('/');
      return mergedString;
  }

   uploadFiles(files: any, maxFileSize: number, path: string, tin: string, callback: any, id?: string, folderId?: string, isMajor?: boolean): void {
      if (files.length === 0) {
         this.busy = false;
         return;
      }
      this.zone.run(() => { callback.init(files); });
      let index = 0;
      const uploadNextFile = () => {
         if (index === files.length) {
            this.busy = false;
            this.service.post('audit').subscribe(() => {
                  // This is intentional.
               },
               (err) => {
                  console.error(err);
               });
            this.zone.run(() => { callback.complete(); });
            return;
         }
         const file: any = files[index++];
         if (file.canceled) {
            this.zone.run(() => { callback.cancelUpload(index - 1, 'Cancel'); });
            uploadNextFile();
            return;
         }
         if (file.size > maxFileSize) {
            this.zone.run(() => { callback.limitExceed(index - 1, file.size); });
            uploadNextFile();
            return;
         }
         if (/[/\\:*?"<>|%]/.test(file.name)) {
            this.translate.get('alert.create_folder_error').subscribe(s=>{
               this.toast.error(s);});
            this.zone.run(() => { callback.specialCharacter(index - 1); });
            uploadNextFile();
            return;
         }
         this.init().subscribe(result => {
            const key = result.key;
            let offset = 0;
            const xhr = new XMLHttpRequest();
            const send = () => {
               const end = Math.min(offset + this.chunkSize, file.size);
               xhr.open('PUT', this.service.url + '/upload/' + key, true);
               xhr.setRequestHeader('x-auth-token', this.service.user.token);
               xhr.setRequestHeader('Content-Range', 'bytes ' + offset + '-' + (end - 1) + '/' + file.size);
               const blob = file.slice.call(file, offset, end);
               xhr.send(blob);
            };
            xhr.onload = () => {
               if (xhr.status === 308) {
                  if (file.canceled) {
                     this.service.post('/cancel/upload/' + key, null).subscribe(() => {
                        this.zone.run(() => {
                           callback.cancelUpload(index - 1, 'Cancel');
                        });
                        uploadNextFile();
                     });
                     return;
                  }
                  const progress = Math.floor(offset * 100 / file.size);
                  this.zone.run(() => { callback.progress(index - 1, progress); });
                  offset += this.chunkSize;
                  send();
               } else if (xhr.status === 201) {
                  this.zone.run(() => { callback.progress(index - 1, 100); });
                  this.finish(key, path, file, tin, id, folderId, isMajor).subscribe(() => {
                     file.finished = true;
                     uploadNextFile();
                  }, error => {
                     if (error.status === 413) {
                        this.zone.run(() => { callback.quotaExceed(index - 1, file.size, error.message); });
                     } else if (error.status === 403) {
                        file.canceled = true;
                        this.zone.run(() => { callback.cancelUpload(index - 1, 'Access Denied'); });
                     } else if (error.status === 500) {
                        file.canceled = true;
                        let msg;
                        if (error.message.message) {
                           msg = error.message.message;
                           if (msg.includes('occurred during upload of new content')) {
                              this.translate.get('alert.folder-already-exists').subscribe(s => {
                                 msg = s;
                              });
                           }
                        } else if (error.message.error) {
                           msg = error.message.error;
                        } else if (error.message) {
                           msg = error.message;
                        }
                        this.zone.run(() => { callback.cancelUpload(index - 1, msg); });
                     }
                     file.error = error.message;
                     uploadNextFile();
                  });
               }
            };
            send();
         }, error => {
            file.error = error;
            uploadNextFile();
         });
      };
      uploadNextFile();
   }

   private init(): Observable<any> {
      return this.service.get('/upload');
   }

   private finish(key: string, path: string, file: any, tin: string, id?: string, folderId?: string, isMajor?: boolean): Observable<any> {
      let filePath = file.fullPath || file.name;
      filePath = filePath.indexOf('/') === 0 ? filePath.substring(1) : filePath;
      if (isMajor === undefined) {
         isMajor = true;
      }
      const body = {
         name: filePath,
         destination: path,
         mimeType: file.type,
         tin: tin,
         id: id,
         folderId: folderId,
         isMajor: isMajor
      };
      return this.service.finishUpload(key, body);
   }
}
